import { Button, InlineFormInput } from '@platform/components';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useSearch } from 'wouter';
import { MovingWorldsBannerLogoLink } from '../../layout/MovingWorldsLogo';
import { ROUTES } from '../../routes/consts';
import { ForgotPasswordLink } from '../components/ForgotPasswordLink';
import { GoogleSignInButton } from '../components/google/GoogleSignInButton';
import { LinkedInSignInButton } from '../components/linkedin/LinkedinSignInButton';
import { LogInBackground } from '../components/LogInBackground';
import { SignUpDivider } from '../components/SignUpDivider';
import { EmailOrPasswordIncorrectBox } from './EmailOrPasswordIncorrectBox';

type FormValues = {
  email: string;
  password: string;
};

export const HomeSignIn = () => {
  const {
    register,
    handleSubmit,
    formState,
  } = useForm<FormValues>({ mode: 'onBlur' });
  const { errors, isSubmitting } = formState;

  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [loginTarget, setLoginTarget] = useState('http://localhost:8000/api/login');
  const searchParams = useSearch();
  const urlSearchParams = new URLSearchParams(searchParams);
  const next = urlSearchParams.get('next');

  useEffect(() => {
    setLoginTarget(
      window && window.location.hostname !== 'localhost' ?
        window.location.origin + '/api/login'
        : 'http://localhost:8000/api/login',
    );
  }, []);

  //We use fetch here instead of the mutation + onSuccess/onError because
  //the cookie needs to be set on login and the mutation onSuccess
  //is in a race condition with the cookie being set
  const login: SubmitHandler<FormValues> = async (data) => {
    setInvalidCredentials(false);
    if (window) {
      const response = await fetch(loginTarget, {
        method: 'POST',
        body: JSON.stringify({
          password: data.password,
          email: data.email,
        }),
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      const responseData = await response.json();
      const nextLocation = next || responseData.next;

      if (response.status === 200) {
        const role = responseData.role;
        if (role === 1) {
          window.location.href =
            import.meta.env.MODE === 'development' ?
              'http://localhost:3001/platform/admin'
              : '/admin';
        } else if (nextLocation) {
          window.location.href = nextLocation;
        } else {
          window.location.href = '/platform/dashboard';
        }
      } else if (response.status === 401) {
        setInvalidCredentials(true);
      }
    }
  };

  return (
    <LogInBackground>
      <div className='h-full'>
        <div className='md:hidden w-full m-auto text-brand-500 h-16 min-h-16 border-b border-gray-100'>
          <MovingWorldsBannerLogoLink
            className='flex justify-center h-full w-full items-center'
            height='22'
          />
        </div>
        <div className='flex flex-col w-full md:w-[400px] px-4 md:px-1 py-8 md:py-0 h-full md:items-center md:justify-center'>
          <div className='flex flex-col gap-8'>
            <h3 className='text-display-sm font-gray-700 font-medium'>
              Welcome back! Log in to see what's new
            </h3>
            <form className='flex flex-col gap-8' name='login' onSubmit={handleSubmit(login)}>
              <InlineFormInput
                label='Email'
                placeholder='Enter your work or personal email'
                autocomplete='email'
                errors={errors.email}
                register={() => {
                  return register('email', {
                    required: { value: true, message: 'Please enter your email address.' },
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Please enter a valid email address.',
                    },
                  });
                }}
              />
              <InlineFormInput
                label='Password'
                inputType='password'
                autocomplete='current-password'
                errors={errors.password}
                placeholder='Enter the password you created'
                register={() => {
                  return register('password', {
                    required: { value: true, message: 'Please enter your password.' },
                  });
                }}
              />
              {invalidCredentials && <EmailOrPasswordIncorrectBox />}
              <SignInButton submitting={isSubmitting} />
            </form>
            <ForgotPasswordLink />
            <SignUpDivider />
            <div className='flex flex-col gap-3'>
              <GoogleSignInButton />
              <LinkedInSignInButton />
            </div>

            <div className='flex justify-center gap-1 pb-8 md:pb-0'>
              Not signed up yet?{' '}
              <Link
                className='text-brand-100 text-center underline'
                to={`~/platform${ROUTES.AUTH}${ROUTES.SIGN_UP}`}
              >
                Join now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </LogInBackground>
  );
};

const SignInButton = ({ submitting }: { submitting: boolean }) => {
  return (
    <Button type='submit' variant='primary' size='md' isDisabled={submitting}>
      {submitting ? 'Logging in...' : 'Log in'}
    </Button>
  );
};
